import { useEffect, useMemo, useState } from 'react';

import {
  activateStandaloneCharges,
  CreateStandaloneChargeActivationDTO,
  getStandaloneChargeActivationStatus,
  StandaloneChargeActivationStateEnum,
  StandaloneChargeActivationStatusDTO
} from '../../../services/standalone-charges';
import { dangerNotification, successNotification } from '../../../services/notification';
import { AddressService } from '../../../services/address';
import { maskCpfOrCnpj, maskPhoneNumber, maskZipCode, unmaskCpfOrCnpj, unmaskPhoneNumber } from '../../../services/masks';
import { isCnpj, isCpf } from '../../../shared/documents';
import { isValidEmail } from '../../../utils/emailValidator';
import { toAbsoluteUrl } from '../../../utils/toAbsoluteUrl';
import { getEstate } from '../../../services/estate';
import { EstateForGetEstatesResponseDTO } from '../../../dtos/estate/estate-for-get-estates-response.dto';

import { initialActivationForm } from '../constants';
import { ActivationFormState, ActivationStep } from '../types';

const PAGARME_BANK_HOLDER_NAME_MAX_LENGTH = 30;

export const normalizePagarmeBankHolderName = (value: string): string =>
  String(value || '').trim().slice(0, PAGARME_BANK_HOLDER_NAME_MAX_LENGTH);

const getActivationPrefillFromEstate = (
  estate: EstateForGetEstatesResponseDTO
): Partial<ActivationFormState> => {
  const cnpj = estate.cnpj ? maskCpfOrCnpj(estate.cnpj) : '';
  const partnerDocument = estate.responsible_document ? maskCpfOrCnpj(estate.responsible_document) : '';
  const zipCode = estate.estate_zip_code ? maskZipCode(estate.estate_zip_code) : '';
  const bankHolderName = normalizePagarmeBankHolderName(estate.company_name || estate.name || '');
  const normalizedState = (estate.estate_state || '').trim().toUpperCase();
  const companyPhone = estate.messaging_phone ? maskPhoneNumber(estate.messaging_phone) : '';
  const partnerPhone = estate.responsible_phone ? maskPhoneNumber(estate.responsible_phone) : '';

  return {
    recipientType: 'corporation',
    email: (estate.email || '').trim(),
    document: cnpj,
    phone: companyPhone,
    companyName: (estate.company_name || '').trim(),
    tradingName: (estate.name || '').trim(),
    partnerName: (estate.responsible_name || '').trim(),
    partnerEmail: (estate.responsible_email || '').trim(),
    partnerDocument,
    partnerPhone,
    zipCode,
    street: (estate.estate_street || '').trim(),
    streetNumber: (estate.estate_number || '').trim(),
    neighborhood: (estate.estate_district || '').trim(),
    city: (estate.estate_city || '').trim(),
    state: normalizedState,
    complementary: (estate.estate_complement || '').trim(),
    bankHolderName,
    bankHolderType: 'company',
    bankHolderDocument: cnpj,
    bankCode: (estate.bank_account?.bank_code || '').trim(),
    bankBranchNumber: (estate.bank_account?.agency_number || '').trim(),
    bankBranchCheckDigit: (estate.bank_account?.agency_dv || '').trim(),
    bankAccountNumber: (estate.bank_account?.account_number || '').trim(),
    bankAccountCheckDigit: (estate.bank_account?.account_dv || '').trim()
  };
};

const toBrazilianBirthdate = (value: string): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [yyyy, mm, dd] = raw.split('-');
    return `${dd}/${mm}/${yyyy}`;
  }
  return raw;
};

const normalizeComplementary = (value: string): string => {
  const normalized = String(value || '').trim();
  return normalized || 'Sem complemento';
};

const toPagarmePhoneNumbers = (
  value: string
): Array<{ ddd: number; number: number; type: 'mobile' }> => {
  let digits = unmaskPhoneNumber(String(value || ''));
  if ((digits.length === 12 || digits.length === 13) && digits.startsWith('55')) {
    digits = digits.slice(2);
  }
  if (!digits || digits.length < 10) return [];

  const ddd = Number(digits.slice(0, 2));
  const number = Number(digits.slice(2));
  if (!ddd || !number) return [];

  return [
    {
      ddd,
      number,
      type: 'mobile'
    }
  ];
};

export const buildQrCodeImageUrl = (value?: string): string => {
  const payload = String(value || '').trim();
  if (!payload) return '';
  if (payload.startsWith('data:image/')) return payload;
  if (payload.startsWith('http://') || payload.startsWith('https://')) return payload;
  return `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(payload)}`;
};

type Params = {
  needsActivationGate: boolean;
  activationEstateId: string;
  appliedFilters: any;
  onEnabled: () => void;
};

export const useStandaloneChargesActivation = ({
  needsActivationGate,
  activationEstateId,
  appliedFilters,
  onEnabled,
}: Params) => {
  const [activationStatus, setActivationStatus] = useState<StandaloneChargeActivationStatusDTO | null>(null);
  const [activationStatusError, setActivationStatusError] = useState(false);
  const [activationLoading, setActivationLoading] = useState(false);
  const [activationSaving, setActivationSaving] = useState(false);
  const [activationForm, setActivationForm] = useState<ActivationFormState>(initialActivationForm);
  const [activationWizardOpen, setActivationWizardOpen] = useState(false);
  const [activationStep, setActivationStep] = useState<ActivationStep>(1);
  const [activationStepError, setActivationStepError] = useState('');
  const [addressLookupLoading, setAddressLookupLoading] = useState(false);
  const [addressLookupError, setAddressLookupError] = useState<string | null>(null);
  const [addressAutoFilled, setAddressAutoFilled] = useState<string[]>([]);

  const canCreateCharges = !needsActivationGate || Boolean(activationStatus?.standaloneChargesEnabled);
  const activationIsPendingKyc = activationStatus?.activation?.status === StandaloneChargeActivationStateEnum.pagarmePending;
  const activationKycLink = activationStatus?.activation?.kycLinkUrl || '';
  const activationKycToken = activationStatus?.activation?.kycToken || '';
  const activationKycPageUrl = activationKycToken
    ? toAbsoluteUrl(`/biometria/${activationKycToken}`)
    : '';
  const activationKycQrPayload =
    activationStatus?.activation?.kycQrPayload ||
    activationStatus?.activation?.kycLinkUrl ||
    '';
  const activationKycQrImageUrl = buildQrCodeImageUrl(activationKycQrPayload);

  const loadActivationStatus = async () => {
    if (!needsActivationGate) return;
    setActivationLoading(true);
    try {
      const status = await getStandaloneChargeActivationStatus();
      setActivationStatus(status);
      setActivationStatusError(false);
    } catch (error) {
      setActivationStatus({
        estateId: '',
        standaloneChargesEnabled: false,
        activation: null
      });
      setActivationStatusError(true);
    } finally {
      setActivationLoading(false);
    }
  };

  useEffect(() => {
    loadActivationStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsActivationGate]);

  const updateActivationFormField = <K extends keyof ActivationFormState>(
    field: K,
    value: ActivationFormState[K]
  ) => {
    setActivationForm(prev => {
      if (field !== 'recipientType') return { ...prev, [field]: value };

      const nextRecipientType = value as ActivationFormState['recipientType'];
      if (nextRecipientType === 'individual') {
        // Evita reaproveitar o CNPJ (prefill da imobiliária) no passo "Recebedor (PF)".
        return { ...prev, recipientType: nextRecipientType, document: '' };
      }

      return { ...prev, recipientType: nextRecipientType };
    });
  };

  const validateActivationStep = (step: ActivationStep): string | null => {
    const documentDigits = unmaskCpfOrCnpj(activationForm.document);
    const isCorporation = activationForm.recipientType === 'corporation';

    if (step === 1) {
      if (!activationForm.recipientType) return 'Selecione o tipo de cadastro.';
      return null;
    }

    if (step === 2) {
      if (isCorporation) return null;
      if (!isValidEmail(activationForm.email)) return 'Informe um e-mail válido.';
      if (
        (activationForm.recipientType === 'individual' && !isCpf(documentDigits)) ||
        (activationForm.recipientType === 'corporation' && !isCnpj(documentDigits))
      ) {
        return 'Documento inválido para o tipo de recebedor.';
      }
      if (!isCorporation) {
        if (
          !activationForm.name.trim() ||
          !activationForm.birthdate ||
          !activationForm.monthlyIncome ||
          !activationForm.professionalOccupation.trim()
        ) {
          return 'Preencha os dados obrigatórios do recebedor.';
        }
        if (!activationForm.phone || unmaskPhoneNumber(activationForm.phone).length < 10) {
          return 'Informe um telefone válido.';
        }
      }
      return null;
    }

    if (step === 3 && activationForm.recipientType === 'corporation') {
      if (!isValidEmail(activationForm.email)) return 'Informe um e-mail válido da empresa.';
      if (!isCnpj(documentDigits)) return 'Informe um CNPJ válido da empresa.';
      if (!activationForm.phone || unmaskPhoneNumber(activationForm.phone).length < 10) {
        return 'Informe um telefone válido da empresa.';
      }
      if (!activationForm.companyName.trim() || !activationForm.tradingName.trim() || !activationForm.annualRevenue.trim()) {
        return 'Preencha os dados obrigatórios da empresa.';
      }
      if (
        !activationForm.partnerName.trim() ||
        !isValidEmail(activationForm.partnerEmail) ||
        unmaskCpfOrCnpj(activationForm.partnerDocument).length !== 11 ||
        !activationForm.partnerPhone ||
        unmaskPhoneNumber(activationForm.partnerPhone).length < 10 ||
        !activationForm.partnerBirthdate ||
        !activationForm.partnerMonthlyIncome ||
        !activationForm.partnerProfessionalOccupation.trim()
      ) {
        return 'Preencha os dados obrigatórios do responsável legal.';
      }
    }

    if (step === 4) {
      if (
        !activationForm.street.trim() ||
        !activationForm.streetNumber.trim() ||
        !activationForm.neighborhood.trim() ||
        !activationForm.city.trim() ||
        activationForm.state.trim().length !== 2 ||
        unmaskCpfOrCnpj(activationForm.zipCode).length !== 8 ||
        !activationForm.referencePoint.trim()
      ) {
        return 'Preencha corretamente o endereço principal.';
      }
      return null;
    }

    if (step === 5) {
      if (
        !activationForm.bankHolderName.trim() ||
        !activationForm.bankHolderDocument.trim() ||
        !activationForm.bankCode.trim() ||
        !activationForm.bankBranchNumber.trim() ||
        !activationForm.bankAccountNumber.trim() ||
        !activationForm.bankAccountCheckDigit.trim()
      ) {
        return 'Preencha todos os dados bancários obrigatórios.';
      }
      if (unmaskCpfOrCnpj(activationForm.bankHolderDocument) !== documentDigits) {
        return 'Documento do titular da conta deve ser igual ao documento do recebedor.';
      }
    }

    return null;
  };

  const getLastActivationStep = (): ActivationStep => 5;

  const handleNextActivationStep = () => {
    const validationError = validateActivationStep(activationStep);
    if (validationError) {
      setActivationStepError(validationError);
      return;
    }

    setActivationStepError('');
    const lastStep = getLastActivationStep();
    if (activationStep < lastStep) {
      if (activationStep === 1 && activationForm.recipientType === 'corporation') {
        setActivationStep(3);
        return;
      }
      if (activationStep === 2 && activationForm.recipientType !== 'corporation') {
        setActivationStep(4);
        return;
      }
      setActivationStep((prev => (prev + 1) as ActivationStep));
    }
  };

  const closeActivationWizard = () => {
    setActivationWizardOpen(false);
    setActivationStepError('');
    setAddressLookupError(null);
  };

  const handleBackActivationStep = () => {
    setActivationStepError('');
    if (activationStep > 1) {
      if (activationStep === 3 && activationForm.recipientType === 'corporation') {
        setActivationStep(1);
        return;
      }
      if (activationStep === 4 && activationForm.recipientType !== 'corporation') {
        setActivationStep(2);
        return;
      }
      setActivationStep((prev => (prev - 1) as ActivationStep));
      return;
    }
    closeActivationWizard();
  };

  const handleZipCodeLookup = async (zipCodeRaw: string) => {
    const cleanZip = zipCodeRaw.replace(/\D/g, '');
    if (cleanZip.length !== 8) return;

    setAddressLookupLoading(true);
    setAddressLookupError(null);
    setAddressAutoFilled([]);
    try {
      const response = await AddressService.getAddressByZipCode(cleanZip);
      const filled: string[] = [];

      if (response.street) {
        updateActivationFormField('street', response.street);
        filled.push('street');
      }
      if ((response as unknown as { district?: string }).district) {
        updateActivationFormField('neighborhood', (response as unknown as { district?: string }).district || '');
        filled.push('neighborhood');
      }
      if (response.city) {
        updateActivationFormField('city', response.city);
        filled.push('city');
      }
      if (response.state) {
        updateActivationFormField('state', response.state);
        filled.push('state');
      }

      setAddressAutoFilled(filled);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e || '');
      setAddressLookupError(msg || 'CEP não encontrado. Verifique e tente novamente.');
    } finally {
      setAddressLookupLoading(false);
    }
  };

  const openActivationWizard = async () => {
    setActivationStep(1);
    setActivationStepError('');
    setAddressLookupError(null);
    setAddressAutoFilled([]);
    setActivationForm(initialActivationForm);
    setActivationWizardOpen(true);

    if (!activationEstateId) return;

    try {
      const estate = await getEstate(activationEstateId);
      setActivationForm(prev => ({
        ...prev,
        ...getActivationPrefillFromEstate(estate)
      }));
    } catch (error) {
      dangerNotification(
        'Atenção',
        'Não foi possível pré-preencher os dados da imobiliária. Você pode seguir preenchendo manualmente.'
      );
    }
  };

  const handleActivate = async () => {
    const lastStep = getLastActivationStep();
    for (let step = 1; step <= lastStep; step += 1) {
      const validationError = validateActivationStep(step as ActivationStep);
      if (validationError) {
        setActivationStep(step as ActivationStep);
        setActivationStepError(validationError);
        return;
      }
    }

    setActivationSaving(true);
    try {
      const commonRegisterInformation = {
        email: activationForm.email.trim().toLowerCase(),
        document: unmaskCpfOrCnpj(activationForm.document),
        type: activationForm.recipientType
      };

      const payload: CreateStandaloneChargeActivationDTO = {
        recipientType: activationForm.recipientType,
        registerInformation: {
          ...commonRegisterInformation,
          ...(activationForm.recipientType === 'corporation'
            ? {
              company_name: activationForm.companyName.trim(),
              trading_name: activationForm.tradingName.trim(),
              annual_revenue: Number(activationForm.annualRevenue),
              phone_numbers: toPagarmePhoneNumbers(activationForm.phone),
              main_address: {
                street: activationForm.street.trim(),
                complementary: normalizeComplementary(activationForm.complementary),
                street_number: activationForm.streetNumber.trim(),
                neighborhood: activationForm.neighborhood.trim(),
                city: activationForm.city.trim(),
                state: activationForm.state.trim().toUpperCase(),
                zip_code: unmaskCpfOrCnpj(activationForm.zipCode),
                reference_point: activationForm.referencePoint.trim()
              },
              managing_partners: [
                {
                  name: activationForm.partnerName.trim(),
                  email: activationForm.partnerEmail.trim().toLowerCase(),
                  document: unmaskCpfOrCnpj(activationForm.partnerDocument),
                  type: 'individual',
                  mother_name: activationForm.partnerMotherName.trim() || undefined,
                  birthdate: toBrazilianBirthdate(activationForm.partnerBirthdate),
                  monthly_income: Number(activationForm.partnerMonthlyIncome),
                  professional_occupation: activationForm.partnerProfessionalOccupation.trim(),
                  self_declared_legal_representative: true,
                  phone_numbers: toPagarmePhoneNumbers(activationForm.partnerPhone),
                  address: {
                    street: activationForm.partnerStreet.trim() || activationForm.street.trim(),
                    complementary: normalizeComplementary(
                      activationForm.partnerComplementary.trim() ||
                      activationForm.complementary.trim()
                    ),
                    street_number:
                      activationForm.partnerStreetNumber.trim() ||
                      activationForm.streetNumber.trim(),
                    neighborhood:
                      activationForm.partnerNeighborhood.trim() ||
                      activationForm.neighborhood.trim(),
                    city: activationForm.partnerCity.trim() || activationForm.city.trim(),
                    state: (activationForm.partnerState.trim() || activationForm.state.trim()).toUpperCase(),
                    zip_code:
                      unmaskCpfOrCnpj(activationForm.partnerZipCode) ||
                      unmaskCpfOrCnpj(activationForm.zipCode),
                    reference_point:
                      activationForm.partnerReferencePoint.trim() ||
                      activationForm.referencePoint.trim()
                  }
                }
              ]
            }
            : {
              name: activationForm.name.trim(),
              mother_name: activationForm.motherName.trim() || undefined,
              birthdate: toBrazilianBirthdate(activationForm.birthdate),
              monthly_income: Number(activationForm.monthlyIncome),
              professional_occupation: activationForm.professionalOccupation.trim(),
              phone_numbers: toPagarmePhoneNumbers(activationForm.phone),
              address: {
                street: activationForm.street.trim(),
                complementary: normalizeComplementary(activationForm.complementary),
                street_number: activationForm.streetNumber.trim(),
                neighborhood: activationForm.neighborhood.trim(),
                city: activationForm.city.trim(),
                state: activationForm.state.trim().toUpperCase(),
                zip_code: unmaskCpfOrCnpj(activationForm.zipCode),
                reference_point: activationForm.referencePoint.trim()
              }
            })
        },
        defaultBankAccount: {
          holder_name: normalizePagarmeBankHolderName(activationForm.bankHolderName),
          holder_type: activationForm.bankHolderType,
          holder_document: unmaskCpfOrCnpj(activationForm.bankHolderDocument),
          bank: activationForm.bankCode.trim(),
          branch_number: activationForm.bankBranchNumber.trim(),
          branch_check_digit: activationForm.bankBranchCheckDigit.trim() || undefined,
          account_number: activationForm.bankAccountNumber.trim(),
          account_check_digit: activationForm.bankAccountCheckDigit.trim(),
          type: activationForm.bankAccountType
        }
      };

      const status = await activateStandaloneCharges(payload);
      setActivationStatus(status);
      const isEnabled = Boolean(status?.standaloneChargesEnabled);
      successNotification(
        'Sucesso',
        isEnabled ? 'Ativação concluída com sucesso!' : 'Cadastro enviado. Conclua a biometria para liberar emissão de cobranças.'
      );
      closeActivationWizard();
      setActivationStep(1);
      if (isEnabled) {
        onEnabled();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      dangerNotification('Erro', message || 'Não foi possível concluir a ativação da imobiliária.');
    } finally {
      setActivationSaving(false);
    }
  };

  return {
    activationStatus,
    activationStatusError,
    activationLoading,
    activationSaving,
    activationForm,
    setActivationForm,
    activationWizardOpen,
    activationStep,
    activationStepError,
    addressLookupLoading,
    addressLookupError,
    addressAutoFilled,
    canCreateCharges,
    activationIsPendingKyc,
    activationKycLink,
    activationKycToken,
    activationKycPageUrl,
    activationKycQrImageUrl,
    loadActivationStatus,
    updateActivationFormField,
    openActivationWizard,
    closeActivationWizard,
    handleNextActivationStep,
    handleBackActivationStep,
    handleZipCodeLookup,
    handleActivate
  };
};

